import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import {
    FilterOutlined,
    ProfileOutlined,
    ReconciliationOutlined,
    SearchOutlined,
    SettingOutlined,
} from '@ant-design/icons';

import { Button, Card, Col, Empty, Modal, Row, Table, Typography } from 'antd';

import SearchDropDown from 'components/SalesOrders/DropDowns/SearchDropDown';
import StatusDropDown from 'components/SalesOrders/DropDowns/StatusDropDown';
import UIStore from '@/stores/UIStore';
import 'styles/bordered-card.css';
import * as S from '../styles/Home';
import DateRange from 'components/SalesOrders/DropDowns/DateRange';

import RequestUpdateForm from '../components/RequestUpdateForm/RequestUpdateForm.component';
import ContactRepForm from 'components/ContactRepForm';
import { SalesOrderProps } from 'types/types';
import SalesOrderStore from 'stores/SalesOrderStore';
import { observer } from 'mobx-react';
import { ColumnProps } from 'antd/es/table';
import SimpleResponsiveTable, { FORMAT_TYPES } from '../components/common/SimpleResponsiveTable';
import { CustomResponsiveTable } from '../components/common/SimpleResponsiveTable';
import { Price, ErrorRender } from '@partsbadger/library';

import RequestOrderAdjustment from 'components/SalesOrders/RequestOrderAdjustment';

import UserStore from 'stores/UserStore';
import Verify from 'components/Verify';
import dayjs from 'dayjs';

const TOTAL_DATA_INDEX = 'grand_total';

const COLUMNS_TABLE_FORMATS = [{ column: TOTAL_DATA_INDEX, format: FORMAT_TYPES.money }];

interface OrderFilters {
    search?: string;
    customer_status?: string;
    purchase_order?: string;
    required_ship_date_min?: string;
    required_ship_date_max?: string;
    invoice_date_min?: string;
    invoice_date_max?: string;
}

const { Title, Text } = Typography;

const Home = () => {
    const [completed_orders_filters, setCompletedOrderFilters] = useState<OrderFilters>();
    const [open_orders_filters, setOpenOrderFilters] = useState<OrderFilters>();
    const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrderProps>();
    const [show_contact_to_rep, setShowContactRep] = useState(false);
    const [emailSubject, setEmailSubject] = useState<string>();
    const [showRequestOrderAdjustment, setShowRequestOrderAdjustment] = useState<boolean>(false);

    useEffect(() => {
        if (UserStore?.isVerified !== undefined) {
            SalesOrderStore.getCompletedOrders(completed_orders_filters);
        }
    }, [completed_orders_filters]);

    useEffect(() => {
        if (UserStore?.isVerified !== undefined) {
            SalesOrderStore.getOpenOrders(open_orders_filters);
        }
    }, [open_orders_filters]);

    const columns_open_orders: ColumnProps<SalesOrderProps>[] = [
        {
            title: 'Order Number',
            dataIndex: 'name',
            width: 120,
            filterDropdown: (
                <SearchDropDown
                    current={open_orders_filters?.search}
                    search={(value: any) => {
                        setOpenOrderFilters({
                            ...open_orders_filters,
                            search: value,
                        });
                    }}
                />
            ),
            filterIcon: <SearchOutlined style={{ color: open_orders_filters?.purchase_order ? '#c00' : '#bfbfbf' }} />,
            render: (text, record) => <Link to={`/my-account/orders/${record.id}`}>{text}</Link>,
        },
        {
            title: 'PO Number',
            dataIndex: 'purchase_order',
            width: 120,
            filterDropdown: (
                <SearchDropDown
                    current={open_orders_filters?.purchase_order}
                    search={value => {
                        setOpenOrderFilters({
                            ...open_orders_filters,
                            purchase_order: value,
                        });
                    }}
                />
            ),
            filterIcon: <SearchOutlined style={{ color: open_orders_filters?.purchase_order ? '#c00' : '#bfbfbf' }} />,
            render: (text, record) => <Link to={`/my-account/orders/${record.id}`}>{text}</Link>,
        },
        {
            title: 'Date',
            dataIndex: 'created_time',
            width: 110,
            render: (text, record) => {
                return (
                    <Link to={`/my-account/orders/${record.id}/`}>
                        <div>{dayjs(record.created_time).format('YYYY-MM-DD')}</div>
                        <div className={'italic'}>By {record.contact?.fullname}</div>
                    </Link>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'customer_status',
            width: 110,
            filterDropdown: (
                <StatusDropDown
                    current={open_orders_filters?.customer_status}
                    status={value => {
                        setOpenOrderFilters({
                            ...open_orders_filters,
                            customer_status: value,
                        });
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
            render: (text, record) => <Link to={`/my-account/orders/${record.id}`}>{text}</Link>,
        },
        {
            title: 'Estimated Ship Date',
            dataIndex: 'required_ship_date',
            width: 130,
            filterDropdown: (
                <DateRange
                    current={[
                        open_orders_filters?.required_ship_date_min || '',
                        open_orders_filters?.required_ship_date_max || '',
                    ]}
                    search={(min, max) => {
                        setOpenOrderFilters({
                            ...open_orders_filters,
                            required_ship_date_min: min,
                            required_ship_date_max: max,
                        });
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
            // sorter: true,
            render: (text, record) =>
                text ? (
                    <Link to={`/my-account/orders/${record.id}`}>{text.substring(0, 10)}</Link>
                ) : (
                    <Link to={`/my-account/orders/${record.id}`}>{'---'}</Link>
                ),
        },
        {
            title: 'Total',
            dataIndex: TOTAL_DATA_INDEX,
            width: 110,
            render: (text, record) => (
                <Link to={`/my-account/orders/${record.id}`}>
                    <Price value={record.grand_total} />
                </Link>
            ),
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            width: 170,
            align: 'center',
            render: (text, record) => (
                <>
                    <Button
                        id="action-button"
                        type="primary"
                        size="small"
                        onClick={async () => {
                            await SalesOrderStore.setOrder(record);
                            setShowRequestOrderAdjustment(true);
                        }}
                        className="second-step"
                    >
                        Request Update
                    </Button>
                </>
            ),
        },
    ];

    const columns_completed_orders: ColumnProps<SalesOrderProps>[] = [
        {
            title: 'Order Number',
            dataIndex: 'name',
            width: 120,
            filterDropdown: (
                <SearchDropDown
                    current={completed_orders_filters?.search}
                    search={(value: any) => {
                        setCompletedOrderFilters({
                            ...completed_orders_filters,
                            search: value,
                        });
                    }}
                />
            ),
            filterIcon: <SearchOutlined style={{ color: completed_orders_filters?.search ? '#c00' : '#bfbfbf' }} />,
            render: (text, record) => <Link to={`/my-account/orders/${record.id}`}>{text}</Link>,
        },
        {
            title: 'PO Number',
            dataIndex: 'purchase_order',
            width: 120,
            filterDropdown: (
                <SearchDropDown
                    current={completed_orders_filters?.purchase_order}
                    search={value => {
                        setCompletedOrderFilters({
                            ...completed_orders_filters,
                            purchase_order: value,
                        });
                    }}
                />
            ),
            filterIcon: (
                <SearchOutlined style={{ color: completed_orders_filters?.purchase_order ? '#c00' : '#bfbfbf' }} />
            ),
        },
        {
            title: 'Date',
            dataIndex: 'created_time',
            width: 110,
            render: (text, record) => {
                return (
                    <div>
                        <div>{dayjs(record.created_time).format('YYYY-MM-DD')}</div>
                        <div className={'italic'}>By {record.contact?.fullname}</div>
                    </div>
                );
            },
        },
        {
            title: 'Completed Date',
            dataIndex: 'invoice_date',
            width: 120,
            filterDropdown: (
                <DateRange
                    current={[
                        completed_orders_filters?.invoice_date_min || '',
                        completed_orders_filters?.invoice_date_max || '',
                    ]}
                    search={(min, max) => {
                        setCompletedOrderFilters({
                            ...completed_orders_filters,
                            invoice_date_min: min,
                            invoice_date_max: max,
                        });
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
            // sorter: true,
            render: text => (text ? text.substring(0, 10) : '---'),
        },
        {
            title: 'Total',
            dataIndex: TOTAL_DATA_INDEX,
            width: 110,
            render: (text, record) => <Price value={record.grand_total} />,
        },
        // {
        //     title: 'Shipments',
        //     dataIndex: 'shipments',
        //     render: (text, record) =>
        //         <Button id="action-button" type="primary" onClick={()=>{
        //            //requets updated function pending
        //         }} >Tracking and Documents</Button>

        // },
        {
            title: 'Actions',
            dataIndex: 'actions',
            width: 170,
            align: 'center',
            render: (text, record) => (
                <Button
                    id="action-button"
                    type="primary"
                    size="small"
                    onClick={() => {
                        setEmailSubject(record.name);
                        setShowContactRep(true);
                        setSelectedSalesOrder(record);
                    }}
                >
                    Contact Support
                </Button>
            ),
        },
    ];

    const history = useHistory();

    useEffect(() => {
        UIStore.setSelectedKey(['home']);
        UIStore.setDocumentTitle('My Account');
        if (UserStore?.isVerified !== undefined) {
            UserStore?.isVerified && SalesOrderStore.getCompletedOrders();
            UserStore?.isVerified && SalesOrderStore.getOpenOrders();
        }
        checkRapidQuote();
    }, []);

    const checkRapidQuote = () => {
        if (UserStore?.isVerified) {
            const quote_token = window.localStorage.getItem('quote_token');
            if (quote_token) {
                UserStore.associateQuote(quote_token).then(res => {
                    const quote_id = res?.quote_id || null;
                    window.localStorage.removeItem('quote_token');
                    window.location.replace('/my-account/quotes/' + Number(quote_id) + '/continue/');
                });
            }
        }
    };

    const emptyTable = () => {
        // if (status || search || dates) {
        //     return (
        //         <Empty
        //             image={Empty.PRESENTED_IMAGE_SIMPLE}
        //             description={<span>Could not find anything with your search</span>}>
        //         </Empty>);
        // }
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <span style={{ color: '#000' }}>
                        Place an order with PartsBadger and see the status of your orders here!
                    </span>
                }
            >
                <Button style={{ textAlign: 'center' }} type="primary">
                    <Link to="/my-account/quotes/new-quote">Submit an RFQ</Link>
                </Button>
            </Empty>
        );
    };

    const completed_orders = SalesOrderStore.completed_orders;
    const open_orders = SalesOrderStore.open_orders;

    return UserStore?.isVerified ? (
        <div className="container mt-4">
            <Row align="stretch" gutter={[8, 0]} className="mb-4">
                <Col md={8} span={24}>
                    <div className="ant-card border h-100 d-flex flex-column justify-content-between p-3">
                        <Title level={3}>Orders</Title>
                        <Text className="mb-0">
                            Stay up to date with your Orders, create new ones or request an invoice for completed
                            orders.
                        </Text>
                        <Link to="/my-account/orders" className="ant-btn ant-btn-default w-100 mt-auto">
                            View Orders
                        </Link>
                    </div>
                </Col>

                <Col md={8} span={24}>
                    <div className="ant-card border h-100 d-flex flex-column justify-content-between p-3">
                        <Title level={3}>Quotes</Title>
                        <Text className="mb-0">Create your own Quotes, customize, and place an Order.</Text>
                        <Link to="/my-account/quotes" className="ant-btn ant-btn-default w-100 mt-auto">
                            View Quotes
                        </Link>
                    </div>
                </Col>

                <Col md={8} span={24}>
                    <div className="ant-card border h-100 d-flex flex-column justify-content-between p-3">
                        <Title level={3}>Order Requirements</Title>
                        <Text className="mb-0">Fine-grained information to get your Orders right.</Text>
                        <Link
                            to="/my-account/settings/order-requirements"
                            className="ant-btn ant-btn-default w-100 mt-auto"
                        >
                            View Order Requirements
                        </Link>
                    </div>
                </Col>
            </Row>

            <Title level={2}>Open Orders</Title>
            <Card>
                <CustomResponsiveTable
                    columns={columns_open_orders}
                    loading={SalesOrderStore.loading_open_orders}
                    data={open_orders}
                    emptyTable={emptyTable}
                    columnsFormat={COLUMNS_TABLE_FORMATS}
                    className="first-step"
                />
            </Card>

            <Title level={2}>Completed Orders</Title>
            <Card>
                <CustomResponsiveTable
                    columns={columns_completed_orders}
                    loading={SalesOrderStore.loading_completed_orders}
                    data={completed_orders}
                    emptyTable={emptyTable}
                    columnsFormat={COLUMNS_TABLE_FORMATS}
                />
            </Card>

            {/* <Modal
                closable={true}
                title="Request Update"
                open={show_request_update}
                onCancel={() => setShowRequestUpdateModal(false)}
                destroyOnClose={true}
                footer={null}
            >
                {sales_order && (
                    <RequestUpdateForm handleClose={() => setShowRequestUpdateModal(false)} sales_order={sales_order} />
                )}
            </Modal> */}

            {showRequestOrderAdjustment && (
                <RequestOrderAdjustment
                    showRequestOrderAdjustment={showRequestOrderAdjustment}
                    onClose={() => {
                        setShowRequestOrderAdjustment(false);
                    }}
                />
            )}

            {show_contact_to_rep && !!UserStore.user?.id && (
                <ContactRepForm
                    emailSubject={emailSubject}
                    current_user={UserStore.user}
                    handleClose={() => setShowContactRep(false)}
                    order={selectedSalesOrder}
                />
            )}
        </div>
    ) : (
        <Verify />
    );
};

export default observer(Home);
